"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { UUID } from "node:crypto";
import { AlertTriangle, ExternalLink, FileText, Trash2 } from "lucide-react";
import { useClientGroups, useProposalMutations } from "@stockline/web";
import { useTranslation } from "react-i18next";
import type { Proposal } from "@stockline/shared/types/ai/proposal";
import type { ProductResponse } from "@/app/api/types/product";
import { useGlobalProducts } from "@/query/hooks/useUnifiedCache";
import { Input } from "@stockline/ui/components/input";
import { useRecapPricesOffline } from "@/app/recap/hooks/useRecapPricesOffline";
import { useChatMediaPreview } from "@/app/ai/hooks/useChatMediaPreview";
import { ProductSelectCombo } from "@/app/recap/components/ui/ProductSelectCombo";
import type { ProposalCardInnerProps } from "./proposal-card-shared";
import { ProposalCardShell } from "./ProposalCardShell";
import { SourceDocumentSlideOver } from "./SourceDocumentSlideOver";
import { useSourceDocumentPanel } from "./SourceDocumentPanel";

function getProductId(proposal: Proposal): string | null {
  const id = proposal.payload?.product_id;
  return typeof id === "string" ? id : null;
}

function getSourceLabel(proposal: Proposal): string | null {
  const label = proposal.payload?.source_label;
  return typeof label === "string" && label.length > 0 ? label : null;
}

function getPriceString(proposal: Proposal): string {
  const price = proposal.payload?.price;
  if (price === null || price === undefined) return "";
  return String(price);
}

/** Parse a user-typed price: "12,5" → 12.5. Returns null when non-finite. */
function parsePrice(raw: string): number | null {
  const normalized = raw.replace(",", ".").trim();
  if (normalized === "") return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function ProposalGroupPriceCard(props: ProposalCardInnerProps) {
  const { batch } = props;
  const { t } = useTranslation();
  const { editProposal, removeProposal, addProposal } = useProposalMutations();
  const groups = useClientGroups();
  const productsQuery = useGlobalProducts({ includeAll: true });
  const { priceMaps } = useRecapPricesOffline();

  // Resolve product_id → product so the closed ProductSelectCombo shell can
  // render the name + SKU immediately. Without this, the lazy combo's shell
  // (which derives its display from the `product` prop only) shows the
  // "Match a product" placeholder until the user hovers a row and the full
  // editor hydrates and resolves the product from `value`.
  const productMap = useMemo(() => {
    const products =
      (productsQuery.data as ProductResponse[] | undefined) ?? [];
    return new Map(products.map((p) => [p.id as string, p]));
  }, [productsQuery.data]);
  // Only the existence flag is needed here — pass downloadBlob=false so the
  // card never eagerly fetches the .xlsx (the slide-over downloads on open).
  const { hasSource } = useChatMediaPreview(batch.id, false);
  // When a push-layout provider is mounted (AI chat / proposals page) the source
  // opens in the resizable side panel that reflows the page. Hosts without a
  // provider fall back to the legacy fixed overlay below.
  const sourcePanel = useSourceDocumentPanel();
  const [sourceOpen, setSourceOpen] = useState(false);

  const isPending = batch.status === "pending";

  const selectedGroupId = useMemo(() => {
    const id = batch.proposals[0]?.payload?.client_group_id;
    return typeof id === "string" ? id : null;
  }, [batch.proposals]);

  const currentPriceFor = useCallback(
    (productId: string | null): number | undefined => {
      if (!selectedGroupId || !productId) return undefined;
      return priceMaps.groupPrices.get(selectedGroupId)?.get(productId);
    },
    [priceMaps.groupPrices, selectedGroupId],
  );

  const unresolvedCount = batch.proposals.filter(
    (p) => !getProductId(p),
  ).length;

  // Move every row to the newly chosen group together — the AI stages a single
  // group per batch, so the selector edits all proposals in lockstep.
  const handleGroupChange = useCallback(
    async (newGroupId: string) => {
      for (const p of batch.proposals) {
        await editProposal(batch.id, p.id, { client_group_id: newGroupId });
      }
    },
    [batch.id, batch.proposals, editProposal],
  );

  const handleProductChange = useCallback(
    (
      proposalId: string,
      productId: string | null,
      product?: ProductResponse | null,
    ) => {
      editProposal(batch.id, proposalId, {
        product_id: productId,
        product_name: product?.name ?? null,
        source_label: null,
      }).catch(console.error);
    },
    [batch.id, editProposal],
  );

  const handlePriceCommit = useCallback(
    (proposalId: string, raw: string) => {
      const parsed = parsePrice(raw);
      if (parsed === null) return;
      editProposal(batch.id, proposalId, { price: parsed }).catch(
        console.error,
      );
    },
    [batch.id, editProposal],
  );

  const handleRemove = useCallback(
    (proposalId: string) => {
      removeProposal(batch.id, proposalId).catch(console.error);
    },
    [batch.id, removeProposal],
  );

  const handleAdd = useCallback(
    (productId: string | null, product?: ProductResponse | null) => {
      if (!productId || !selectedGroupId) return;
      addProposal(batch.id, {
        entityType: "client_group_product_price",
        payload: {
          client_group_id: selectedGroupId,
          product_id: productId,
          product_name: product?.name ?? null,
          price:
            priceMaps.groupPrices.get(selectedGroupId)?.get(productId) ?? 0,
        },
      }).catch(console.error);
    },
    [batch.id, addProposal, selectedGroupId, priceMaps.groupPrices],
  );

  const approveDisabled = unresolvedCount > 0 || !selectedGroupId;

  // Hide the shortcut when the card is already rendered inside the proposals
  // page — the link would just point at the current view.
  const onProposalsPage = useLocation().pathname.startsWith("/ai-proposals");
  const headerAction = onProposalsPage ? undefined : (
    <Link
      to="/ai-proposals"
      data-testid="group-price-open-proposals"
      title={t("aiProposals.priceReview.openInProposals")}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline shrink-0"
    >
      <ExternalLink className="h-3 w-3" />
      {t("aiProposals.priceReview.openInProposals")}
    </Link>
  );

  return (
    <ProposalCardShell
      {...props}
      approveDisabled={approveDisabled}
      headerAction={headerAction}
    >
      <div className="px-3 pb-2 space-y-2">
        {/* Group selector + source-document button */}
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground shrink-0">
            {t("aiProposals.priceReview.group")}
          </label>
          <select
            value={selectedGroupId ?? ""}
            disabled={!isPending}
            onChange={(e) =>
              handleGroupChange(e.target.value).catch(console.error)
            }
            data-testid="group-price-group-select"
            className="flex-1 h-7 rounded-md border border-border bg-background px-2 text-xs"
          >
            <option value="" disabled>
              {t("aiProposals.priceReview.noGroupSelected")}
            </option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          {hasSource && (
            <button
              type="button"
              onClick={() =>
                sourcePanel
                  ? sourcePanel.openSource(batch.id)
                  : setSourceOpen(true)
              }
              data-testid="group-price-source-button"
              title={t("aiProposals.priceReview.sourceDocument")}
              className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-border text-xs text-muted-foreground hover:text-foreground"
            >
              <FileText className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Block-validate banner */}
        {unresolvedCount > 0 && (
          <div
            className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
            data-testid="group-price-approve-blocked-banner"
          >
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>
              {t("aiProposals.priceReview.rowsNeedProduct", {
                count: unresolvedCount,
              })}
            </span>
          </div>
        )}

        {/* Price table */}
        <div className="rounded-md bg-background/60 border border-border/50 overflow-hidden">
          <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 border-b border-border/30 text-xs text-muted-foreground font-medium">
            <span className="w-4 shrink-0" aria-hidden />
            <span className="flex-1 min-w-0">
              {t("aiProposals.field.product")}
            </span>
            <span className="w-20 text-right">
              {t("aiProposals.priceReview.currentPrice")}
            </span>
            <span className="w-24 text-right">
              {t("aiProposals.priceReview.proposedPrice")}
            </span>
            {isPending && <span className="w-6" />}
          </div>

          {batch.proposals.map((proposal) => (
            <GroupPriceRow
              key={proposal.id}
              proposal={proposal}
              product={productMap.get(getProductId(proposal) ?? "") ?? null}
              isPending={isPending}
              currentPrice={currentPriceFor(getProductId(proposal))}
              onProductChange={handleProductChange}
              onPriceCommit={handlePriceCommit}
              onRemove={handleRemove}
            />
          ))}

          {/* Add-line row */}
          {isPending && selectedGroupId && (
            <div
              className="flex items-center gap-1 px-2 py-1.5 border-t border-border/30"
              data-testid="group-price-add"
            >
              <span className="w-4 shrink-0" aria-hidden />
              <div className="flex-1 min-w-0">
                <ProductSelectCombo
                  value={null}
                  onChange={(pid, _sub, product) => handleAdd(pid, product)}
                  placeholder={t("aiProposals.priceReview.addLine")}
                />
              </div>
            </div>
          )}
        </div>

        <div className="text-xs text-muted-foreground text-right">
          {batch.proposals.length} {t("aiProposals.priceReview.lines")}
        </div>
      </div>

      {!sourcePanel && (
        <SourceDocumentSlideOver
          batchId={batch.id}
          open={sourceOpen}
          onClose={() => setSourceOpen(false)}
        />
      )}
    </ProposalCardShell>
  );
}

function GroupPriceRow({
  proposal,
  product,
  isPending,
  currentPrice,
  onProductChange,
  onPriceCommit,
  onRemove,
}: {
  readonly proposal: Proposal;
  readonly product: ProductResponse | null;
  readonly isPending: boolean;
  readonly currentPrice: number | undefined;
  readonly onProductChange: (
    proposalId: string,
    productId: string | null,
    product?: ProductResponse | null,
  ) => void;
  readonly onPriceCommit: (proposalId: string, raw: string) => void;
  readonly onRemove: (proposalId: string) => void;
}) {
  const { t } = useTranslation();
  const productId = getProductId(proposal);
  const sourceLabel = getSourceLabel(proposal);
  // A just-added line keeps a temp id until SSE swaps in the server's real id.
  // edit/remove against a temp id silently no-op, so lock the row until then.
  const isOptimistic =
    (proposal as Proposal & { __optimistic?: boolean }).__optimistic === true;
  const externalPrice = getPriceString(proposal);
  const [priceDraft, setPriceDraft] = useState(externalPrice);
  // Re-sync the draft when the proposal's price changes upstream (SSE / AI
  // revision) without clobbering an in-progress local edit: local typing only
  // touches priceDraft, so the draft tracks the *external* value and only
  // resets when that external value actually changes.
  const lastExternalPrice = useRef(externalPrice);
  useEffect(() => {
    if (externalPrice !== lastExternalPrice.current) {
      lastExternalPrice.current = externalPrice;
      setPriceDraft(externalPrice);
    }
  }, [externalPrice]);

  const proposedNum = parsePrice(priceDraft);
  const delta =
    currentPrice !== undefined && proposedNum !== null
      ? proposedNum - currentPrice
      : null;

  // Two row states (frontend-only): a product is matched (product_id set), or
  // the AI only quoted a reference read from the document and the user still
  // needs to match it. Unmatched rows get an amber tint + warning icon so they
  // stand out as a block needing attention; matched rows stay clean.
  const isMatched = productId !== null;

  return (
    <div
      className={`flex items-center gap-1 px-2 py-1 text-sm border-b border-border/30 last:border-b-0 group ${
        isMatched ? "hover:bg-muted/10" : "bg-amber-50/40 dark:bg-amber-900/10"
      }`}
      data-testid={`group-price-row-${proposal.id}`}
      data-matched={isMatched}
    >
      {/* Status indicator — only unmatched rows are flagged; matched rows keep
          a blank spacer so the columns stay aligned. */}
      {isMatched ? (
        <span className="w-4 shrink-0" aria-hidden />
      ) : (
        <span
          className="w-4 shrink-0 flex items-center justify-center"
          title={t("aiProposals.priceReview.needsProduct")}
          data-testid="group-price-status-unmatched"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
        </span>
      )}

      {/* Product picker */}
      <div
        className="flex-1 min-w-0"
        data-testid={`group-price-product-combo-${proposal.id}`}
      >
        <ProductSelectCombo
          value={productId as UUID | null}
          product={product}
          disabled={!isPending || isOptimistic}
          onChange={(pid, _sub, picked) =>
            onProductChange(proposal.id, pid, picked)
          }
          placeholder={sourceLabel ?? t("aiProposals.priceReview.pickProduct")}
          searchSeed={sourceLabel ?? undefined}
        />
        {!productId && sourceLabel && (
          <div
            className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground"
            title={t("aiProposals.priceReview.fromDocument")}
          >
            <FileText className="h-3 w-3 shrink-0" />
            <span className="truncate">{sourceLabel}</span>
          </div>
        )}
      </div>

      {/* Current price */}
      <span
        className="w-20 text-right text-xs text-muted-foreground shrink-0"
        data-testid={`group-price-current-${productId ?? "none"}`}
      >
        {currentPrice === undefined ? "—" : String(currentPrice)}
      </span>

      {/* Proposed price */}
      <div className="w-24 text-right shrink-0">
        {isPending ? (
          <Input
            value={priceDraft}
            disabled={isOptimistic}
            onChange={(e) => setPriceDraft(e.target.value)}
            onBlur={() =>
              !isOptimistic && onPriceCommit(proposal.id, priceDraft)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isOptimistic)
                onPriceCommit(proposal.id, priceDraft);
            }}
            inputMode="decimal"
            title={
              isOptimistic ? t("aiProposals.priceReview.saving") : undefined
            }
            className="h-6 text-xs px-1 text-right"
            data-testid={`group-price-proposed-${proposal.id}`}
          />
        ) : (
          <span className="text-xs font-medium">
            {priceDraft ? `${priceDraft} €` : ""}
          </span>
        )}
        {delta !== null && delta !== 0 && (
          <div
            className={`text-[10px] ${
              delta > 0 ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {delta > 0 ? "+" : ""}
            {delta.toFixed(2)}
          </div>
        )}
      </div>

      {/* Delete */}
      {isPending && (
        <button
          onClick={() => !isOptimistic && onRemove(proposal.id)}
          disabled={isOptimistic}
          className="w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0 disabled:opacity-30"
          title={t("aiProposals.removeProposal")}
          data-testid={`group-price-remove-${proposal.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
